/*
 * NORA PRO chemical compatibility engine.
 * One copy of the lookup and list logic, used by:
 *   - the guide page tool (theme asset assets/nora-chem-engine.js, copied by build.mjs)
 *   - the lookup tests (tools/chem-guide/test-lookups.mjs)
 *   - the assistant Worker (workers/chem-assistant), which only maps words to row ids
 * The dataset decides every answer. Nothing in here invents a rating.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NoraChem = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SPELL = {
    sulfuric: 'sulphuric', sulfate: 'sulphate', sulfur: 'sulphur', aluminum: 'aluminium', tire: 'tyre',
    tires: 'tyres', carburetor: 'carburettor', gasoline: 'petrol', color: 'colour', liter: 'litre',
    liters: 'litres',
  };
  var FAMILY_ORDER = ['acid', 'solvent', 'alkaline'];
  var STOP = ['the', 'for', 'and', 'of', 'to', 'my', 'in', 'is', 'are', 'can', 'does', 'with', 'which', 'what', 'seal', 'seals',
    'sprayer', 'spray', 'spraying', 'use', 'using', 'need', 'needs', 'do', 'how', 'good', 'suit', 'suits', 'work', 'works', 'will',
    'please', 'about', 'best', 'right', 'type', 'strength', 'percent', 'cent', 'per', 'on', 'it', 'its', 'an', 'at', 'be'];

  function norm(s) {
    s = String(s == null ? '' : s).toLowerCase();
    if (s.normalize) s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
    s = s.replace(/&/g, ' and ').replace(/[^a-z0-9%.]+/g, ' ').replace(/(^|\s)\.+|\.+(\s|$)/g, ' ');
    return s.replace(/\s+/g, ' ').trim();
  }

  function spell(s) {
    return norm(s).split(' ').map(function (t) { return SPELL[t] || t; }).join(' ');
  }

  function lev(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > 2) return 3;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j += 1) prev[j] = j;
    for (i = 1; i <= al; i += 1) {
      cur = [i];
      for (j = 1; j <= bl; j += 1) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
      }
      prev = cur;
    }
    return prev[bl];
  }

  // Names we match against: the display name, the name without brackets, each bracket phrase, each alias.
  function labelsOf(row) {
    var out = [row.name];
    var plain = row.name.replace(/\([^)]*\)/g, ' ');
    out.push(plain);
    var m = row.name.match(/\(([^)]*)\)/g) || [];
    m.forEach(function (b) { out.push(b.replace(/[()]/g, '')); });
    (row.aka || []).forEach(function (a) { out.push(a); });
    var seen = {};
    return out.map(spell).filter(function (x) { if (!x || seen[x]) return false; seen[x] = 1; return true; });
  }

  function buildIndex(data) {
    if (data._index) return data._index;
    var idx = data.chemicals.map(function (row) {
      var labels = labelsOf(row);
      var tokens = {};
      labels.forEach(function (l) { l.split(' ').forEach(function (t) { if (t.length > 1) tokens[t] = 1; }); });
      return { row: row, labels: labels, tokens: Object.keys(tokens) };
    });
    Object.defineProperty(data, '_index', { value: idx, enumerable: false });
    return idx;
  }

  // Pull a trailing strength out of free text, for example "hydrochloric acid 10%" or "bleach 12.5 percent".
  function parseQuery(q) {
    var text = String(q == null ? '' : q);
    var strength = null;
    var m = text.match(/(\d+(?:\.\d+)?)\s*(%|per\s*cent|percent)/i);
    if (m) {
      strength = parseFloat(m[1]);
      text = text.replace(m[0], ' ');
    }
    var ph = text.match(/\bph\s*(\d+(?:\.\d+)?)/i);
    var phValue = null;
    if (ph) {
      phValue = parseFloat(ph[1]);
      text = text.replace(ph[0], ' ');
    }
    return { text: text.trim(), strength: strength, ph: phValue };
  }

  function score(entry, q, qTokens) {
    var best = 0;
    var i;
    for (i = 0; i < entry.labels.length; i += 1) {
      var l = entry.labels[i];
      if (l === q) return 100;
      var startsWith = l.indexOf(q + ' ') === 0;
      var endsWith = l.length > q.length && l.lastIndexOf(' ' + q) === l.length - q.length - 1;
      if (startsWith || endsWith) best = Math.max(best, 80);
      else if (q.length >= 3 && l.indexOf(q) > -1) best = Math.max(best, 55);
    }
    if (best >= 80) return best;
    // Every word typed must be found in the row. A partial overlap is never a match, so "sodium chlorate"
    // can not land on "sodium hypochlorite".
    var hit = 0;
    qTokens.forEach(function (t) { if (entry.tokens.indexOf(t) > -1) hit += 1; });
    if (qTokens.length && hit === qTokens.length) best = Math.max(best, 65);
    if (best < 45) {
      // Typos: one slip in each long word, nothing looser.
      var long = qTokens.filter(function (t) { return t.length >= 5; });
      var fuzzy = 0;
      long.forEach(function (t) {
        for (var k = 0; k < entry.tokens.length; k += 1) {
          if (entry.tokens[k].length >= 5 && lev(t, entry.tokens[k]) <= 1) { fuzzy += 1; break; }
        }
      });
      var short = qTokens.filter(function (t) { return t.length < 5; });
      var shortHit = short.filter(function (t) { return entry.tokens.indexOf(t) > -1; }).length;
      if (long.length && fuzzy === long.length && shortHit === short.length) best = Math.max(best, 45);
    }
    return best;
  }

  // Ranked matches for free text. Returns [{row, score}] with score >= 40.
  function search(data, query, limit) {
    var parsed = parseQuery(query);
    var q = spell(parsed.text);
    if (q.length < 2) return [];
    var qTokens = q.split(' ').filter(function (t) { return t.length > 1 && STOP.indexOf(t) === -1; });
    var idx = buildIndex(data);
    var out = [];
    idx.forEach(function (entry) {
      var s = score(entry, q, qTokens.length ? qTokens : [q]);
      if (s >= 40) out.push({ row: entry.row, score: s });
    });
    out.sort(function (a, b) { return b.score - a.score || a.row.name.length - b.row.name.length; });
    return out.slice(0, limit || 8);
  }

  // Matches (score 65 and up) are safe to show as the answer. Suggestions (45 to 64) are typo guesses
  // and must be offered as "did you mean", never shown as an answer.
  function lookup(data, query, limit) {
    var all = search(data, query, limit || 8);
    return {
      parsed: parseQuery(query),
      matches: all.filter(function (x) { return x.score >= 65; }),
      suggestions: all.filter(function (x) { return x.score < 65; }),
    };
  }

  // True when the top match is clearly the one the user meant (no close runner up).
  function isClear(result) {
    var m = result.matches;
    if (!m.length) return false;
    return m.length === 1 || m[0].score - m[1].score >= 15;
  }

  function byId(data, id) {
    for (var i = 0; i < data.chemicals.length; i += 1) if (data.chemicals[i].id === id) return data.chemicals[i];
    return null;
  }

  // The answer for one row. opts: { strength: number|null, hot: boolean }
  function resolve(data, row, opts) {
    opts = opts || {};
    var res = {
      id: row.id, name: row.name, group: row.group,
      rating: row.rating, fams: row.rating === 'ask' ? [] : (row.fams || []).slice(),
      seal: row.seal, sealGroup: row.sealGroup, spec: row.spec || null, text: null,
      max: row.max || null, col: row.col, note: row.note || null,
      needsStrength: false, scale: row.scale || null, hot: false, strength: null,
    };
    if (row.bands && row.bands.length) {
      var s = typeof opts.strength === 'number' && !isNaN(opts.strength) ? opts.strength : null;
      if (s === null) {
        // A cap row is rated as it stands; its single band only matters above the top strength.
        res.needsStrength = !row.cap;
      } else {
        res.strength = s;
        var band = null;
        for (var i = 0; i < row.bands.length; i += 1) {
          if (s <= row.bands[i].upto) { band = row.bands[i]; break; }
        }
        if (band) {
          res.rating = band.rating;
          res.fams = band.rating === 'ask' ? [] : (band.fams || []).slice();
          res.spec = band.spec || null;
          res.text = band.text;
        } else {
          res.rating = 'ask';
          res.fams = [];
          res.text = row.over || 'No source rates a sprayer at this strength. Ask us.';
        }
      }
    }
    if (opts.hot) {
      res.hot = true;
      res.rating = 'ask';
      res.fams = [];
      res.text = data.meta.temperature_note;
    }
    res.famLabels = res.fams.map(function (f) { return data.families[f].label; });
    return res;
  }

  function subsets(arr) {
    var out = [];
    for (var mask = 1; mask < (1 << arr.length); mask += 1) {
      var s = [];
      for (var i = 0; i < arr.length; i += 1) if (mask & (1 << i)) s.push(arr[i]);
      out.push(s);
    }
    out.sort(function (a, b) { return a.length - b.length; });
    return out;
  }

  // Work out how many sprayers a chemical list needs. items: [{row, strength, hot}]
  function planList(data, items) {
    var resolved = items.map(function (it) {
      var r = resolve(data, it.row, { strength: it.strength, hot: it.hot });
      r.input = it;
      return r;
    });
    var asks = resolved.filter(function (r) { return !r.fams.length; });
    var covered = resolved.filter(function (r) { return r.fams.length; });
    var units = [];
    if (covered.length) {
      var best = null;
      subsets(FAMILY_ORDER).forEach(function (set) {
        if (best && set.length > best.length) return;
        var ok = covered.every(function (r) { return r.fams.some(function (f) { return set.indexOf(f) > -1; }); });
        if (!ok) return;
        if (!best) { best = set; return; }
        var groups = function (x) { var g = {}; x.forEach(function (f) { g[data.families[f].sealGroup] = 1; }); return Object.keys(g).length; };
        if (set.length === best.length && groups(set) < groups(best)) best = set;
      });
      units = best || [];
    }
    covered.forEach(function (r) {
      var pick = r.fams.filter(function (f) { return units.indexOf(f) > -1; });
      r.assigned = pick.length ? pick[0] : r.fams[0];
    });
    var sealGroups = {};
    units.forEach(function (f) { sealGroups[data.families[f].sealGroup] = 1; });
    var sealList = Object.keys(sealGroups);
    var mixesSeals = sealList.length > 1;
    var conditional = resolved.filter(function (r) { return r.rating === 'conditional' || r.needsStrength; });
    var messages = [];
    var summary = '';
    if (!resolved.length) messages.push('Add the chemicals you spray.');
    if (units.length === 1) summary = 'One sprayer covers the chemicals we can match: the ' + data.families[units[0]].label.toLowerCase() + '.';
    if (units.length > 1) summary = 'You need ' + units.length + ' sprayers: ' + units.map(function (f) { return 'the ' + data.families[f].label.toLowerCase(); }).join(', ') + '.';
    if (mixesSeals) messages.push('This list mixes VITON and EPDM chemicals, so the seals cannot be shared. Keep each chemical in its own sprayer and rinse between uses.');
    else if (units.length > 1) messages.push('Both sprayers use VITON seals, but the acid and solvent models are different builds. A VITON acid sprayer is not for petroleum solvents, and the reverse.');
    if (asks.length) messages.push(asks.length + (asks.length === 1 ? ' chemical needs' : ' chemicals need') + ' a check with us before you buy: ' + asks.map(function (r) { return r.name; }).join(', ') + '.');
    if (conditional.length) messages.push('Some answers are conditional or need a strength. Check the notes below each one.');
    return {
      items: resolved, asks: asks, covered: covered, units: units,
      unitLabels: units.map(function (f) { return data.families[f].label; }),
      sealGroups: sealList, mixesSeals: mixesSeals, messages: messages, summary: summary,
      oneSprayer: units.length === 1 && !asks.length,
    };
  }

  // Share link payload: "hydrochloric-acid:10,xylene,bleach". Temperature goes in a separate flag.
  function encodeList(entries) {
    return entries.map(function (e) { return e.row.id + (typeof e.strength === 'number' && !isNaN(e.strength) ? ':' + e.strength : ''); }).join(',');
  }
  function decodeList(data, str) {
    var out = [];
    String(str || '').split(',').slice(0, 20).forEach(function (part) {
      var bits = part.split(':');
      var row = byId(data, bits[0].replace(/[^a-z0-9-]/g, ''));
      if (!row) return;
      var s = bits[1] === undefined ? null : parseFloat(bits[1]);
      out.push({ row: row, strength: s === null || isNaN(s) ? null : s });
    });
    return out;
  }

  // Quote form prefill. The quote page reads chemical, seal and note.
  function quoteParams(data, plan, hot) {
    var chem = plan.items.map(function (r) {
      return r.name + (r.strength !== null && r.strength !== undefined ? ' (' + r.strength + (r.scale === 'pH' ? ' pH' : '%') + ')' : '');
    }).join('; ');
    var lines = plan.items.map(function (r) {
      var what = r.fams.length ? data.families[r.assigned || r.fams[0]].label : 'ask us' + (r.spec ? ', ' + r.spec : '');
      return r.name + (r.strength !== null && r.strength !== undefined ? ' ' + r.strength + (r.scale === 'pH' ? ' pH' : '%') : '') + ': ' + what;
    });
    var note = 'From the NORA PRO guide (dataset v' + data.meta.version + '). ' + lines.join('. ') + '.' + (hot ? ' The liquid is warmer than 30 °C.' : '');
    return { chemical: chem, seal: plan.sealGroups.join(' and '), note: note };
  }

  // The models we stock for one or more families, grouped. Klager models are rated on the chart. Other
  // brands share the seal but are not Klager's build. Empty when the dataset has no models.
  function modelsFor(data, fams) {
    var list = data.models || [];
    return (fams || []).filter(function (f, i, a) { return a.indexOf(f) === i && data.families[f]; }).map(function (f) {
      var inFam = list.filter(function (m) { return m.fam === f; });
      return {
        fam: f,
        label: data.families[f].label,
        rated: inFam.filter(function (m) { return m.brand === 'Klager'; }),
        other: inFam.filter(function (m) { return m.brand !== 'Klager'; }),
      };
    }).filter(function (g) { return g.rated.length || g.other.length; });
  }

  return {
    norm: norm, spell: spell, parseQuery: parseQuery, search: search, lookup: lookup, isClear: isClear, byId: byId, resolve: resolve,
    planList: planList, encodeList: encodeList, decodeList: decodeList, quoteParams: quoteParams, modelsFor: modelsFor,
    FAMILY_ORDER: FAMILY_ORDER,
  };
});

/*
 * NORA PRO mix and dose engine.
 * Pure maths for the Dilution Calculator page. No DOM, no network.
 * Used by:
 *   - the page UI (built into the theme asset assets/nora-mix.js by tools/mix-calc/build.mjs)
 *   - the tests (tools/mix-calc/test-engine.mjs) and the copy tables (tools/mix-calc/build-copy.mjs)
 *
 * Every label rate is turned into one number: the share of concentrate in the finished mix, by volume.
 * Volumes are in millilitres inside the engine.
 * It never supplies a product rate. It only converts the rate the user reads off the label.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NoraMix = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Container sizes offered as quick picks. Litres. The per hectare tab offers spray tank sizes instead.
  var SIZES = [0.5, 0.75, 1, 1.5, 5, 8, 10, 12, 16, 20, 200, 1000];
  var TANKS = [15, 100, 200, 400, 600, 1000];
  // Hand, knapsack and small boom sprayers put out roughly 100 to 500 L per hectare; far more is most often an area typed
  // in the wrong unit (DIL-07).
  var OUTPUT_HIGH = 2000;

  // Fixed figures with their sources. The page copy cites the same documents.
  var PRESETS = {
    ppmTargets: [
      { ppm: 100, label: '100 ppm, cold water', src: 'FSANZ cleaning and sanitising fact sheet' },
      { ppm: 50, label: '50 ppm, warm water', src: 'FSANZ cleaning and sanitising fact sheet' },
    ],
    chlorineStrengths: [
      { pct: 4, label: 'Household bleach, about 4%', src: 'FSANZ cleaning and sanitising fact sheet' },
      { pct: 10, label: 'Commercial bleach, about 10%', src: 'FSANZ cleaning and sanitising fact sheet' },
      { pct: 12.5, label: 'Sodium hypochlorite 12.5%', src: 'Common trade strength; check your label' },
    ],
  };
  // No acid, pesticide or termiticide rates here on purpose: they belong to the product maker's label.

  // Reads what people type: "1,000" is a thousand, but "1,5" or "2,25" (a comma as the decimal point) is 1.5 or 2.25.
  function num(x) {
    if (typeof x === 'number') return x;
    if (x === null || x === undefined) return NaN;
    var s = String(x).trim().replace(/\s+/g, '');
    if (/^[-+]?\d+,\d{1,2}$/.test(s)) s = s.replace(',', '.');
    s = s.replace(/,/g, '');
    if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
    return parseFloat(s);
  }
  function pos(x) { return typeof x === 'number' && isFinite(x) && x > 0; }
  function blank(x) { return x === undefined || x === null || String(x).trim() === ''; }

  // A unit typed after the number is fine when it is the unit the box already shows: "5 L", "20 mL", "2%", "100 ppm".
  var UNIT_RE = {
    ml: /\s*ml$/i,
    l: /\s*l(itres?)?$/i,
    pct: /\s*(%|per ?cent)$/i,
    ppm: /\s*ppm$/i,
    out: /\s*l(itres?)?(\s*(\/|per|a)\s*(ha|hectares?))?$/i,
    perha: /\s*(\/|per|a)\s*(ha|hectares?)$/i,
    m2: /\s*(m²|m2|sq ?m|square met(re|er)s?)$/i,
    ha: /\s*(ha|hectares?)$/i,
  };
  // Reads a box: the number, with the box's own unit allowed after it. unit: 'ml', 'l', 'pct', 'ppm', 'out', 'm2', 'ha'.
  function fieldNum(x, unit) {
    if (typeof x === 'number' || x === null || x === undefined) return num(x);
    var s = String(x).trim();
    if (unit === 'ml' || unit === 'l') s = s.replace(UNIT_RE.perha, '');
    if (UNIT_RE[unit]) s = s.replace(UNIT_RE[unit], '');
    return num(s);
  }
  // Letters or symbols left after the number: the visitor typed a unit or words, so ask for the number only.
  function notNumber(x) { return /[^\d\s.,+\-\u2212]/.test(String(x)); }
  // "1:20" in the ratio box is 20; "1 in 40" in the 1 in box is 40 (DIL-05).
  function ratioN(x) { var m = String(x == null ? '' : x).match(/^\s*1\s*:\s*(.+)$/); return m ? num(m[1]) : num(x); }
  function oneInN(x) { var m = String(x == null ? '' : x).match(/^\s*1\s*in\s*(.+)$/i); return m ? num(m[1]) : num(x); }
  function err(field, text) { return { ok: false, field: field, error: text }; }

  // A label rate as data:
  //   { kind: 'ratio',   n }                         1 part concentrate to n parts water (1:n)
  //   { kind: 'onein',   n }                         1 part in n parts of finished mix (1 in n)
  //   { kind: 'per',     amount, unit, perL }        amount (mL or L) in perL litres, made up to volume (see below)
  //   { kind: 'percent', pct }                       per cent of the finished mix, by volume
  //   { kind: 'ppm',     ppm, strength }             target ppm of active from a concentrate of strength %
  //   { kind: 'hectare', amount, unit, output }      label rate per hectare, sprayer output in L per hectare
  // Returns { ok, f } where f is the share of concentrate in the finished mix, or { ok: false, field, error }.
  // field names the box at fault: n, amount, perL, pct, ppm, strength, output.
  //
  // "20 mL per litre of water" or "600 mL in 100 L of water" is made up to volume: labels say to half fill the tank,
  // add the product, then top up to the litres given. So 20 mL per litre is 20 mL in each litre of finished mix.
  // The Termite Barrier Calculator uses the same rule (ALL-03).
  function fraction(rate) {
    if (!rate || !rate.kind) return { ok: false, error: 'Choose how the label gives the rate.' };
    var k = rate.kind;
    var f;
    if (k === 'ratio') {
      if (/^\s*1\s*in\b/i.test(String(rate.n))) return err('n', 'That is a “1 in” rate, which is not the same as a ratio. Choose “1 in ...” above for it.');
      var n = ratioN(rate.n);
      if (blank(rate.n)) return err('n', 'Enter the number of parts water, for example 10 for 1:10.');
      if (!(n >= 0) || !isFinite(n)) return err('n', notNumber(String(rate.n).replace(/^\s*1\s*:/, '')) ? 'Type the parts of water as a number only, for example 20 for 1:20.' : 'Enter the number of parts water, for example 10 for 1:10.');
      f = 1 / (n + 1);
    } else if (k === 'onein') {
      if (/^\s*1\s*:/.test(String(rate.n))) return err('n', 'That is a ratio, which is not the same as “1 in”. Choose “Ratio” above for it.');
      var m = oneInN(rate.n);
      if (!(m >= 1) || !isFinite(m)) return err('n', 'Enter the total parts, for example 20 for 1 in 20. It must be 1 or more.');
      f = 1 / m;
    } else if (k === 'per') {
      var amt = fieldNum(rate.amount, rate.unit === 'l' ? 'l' : 'ml');
      var perL = fieldNum(rate.perL, 'l');
      if (!pos(amt)) return err('amount', notNumber(rate.amount) && !blank(rate.amount) ? 'Type the amount as a number only, for example 20.' : 'Enter the amount of concentrate from the label.');
      if (!pos(perL)) return err('perL', notNumber(rate.perL) && !blank(rate.perL) ? 'Type the litres as a number only, for example 1.' : 'Enter how many litres the label amount is for.');
      var amtMl = rate.unit === 'l' ? amt * 1000 : amt;
      f = amtMl / (perL * 1000);
      if (f > 1 + 1e-12) return err('amount', 'That is more concentrate than the litres it goes in. Check the amount and its unit (mL or L).');
      f = Math.min(f, 1);
    } else if (k === 'percent') {
      var p = fieldNum(rate.pct, 'pct');
      if (!pos(p) || p > 100) return err('pct', notNumber(rate.pct) && !blank(rate.pct) && !/^\s*[\d.,]+\s*%?\s*$/.test(rate.pct) ? 'Type the per cent as a number only, for example 2.' : 'Enter a per cent between 0 and 100.');
      f = p / 100;
    } else if (k === 'ppm') {
      var ppm = fieldNum(rate.ppm, 'ppm');
      var s = fieldNum(rate.strength, 'pct');
      if (!pos(ppm)) return err('ppm', 'Enter the strength you want in ppm (parts per million), for example 100.');
      if (blank(rate.strength)) return err('strength', 'Pick how strong your bleach is, or type the strength from its label.');
      if (!pos(s) || s > 100) return err('strength', 'Enter how strong your bleach is, in per cent from its label, above 0 and up to 100.');
      f = ppm / (s * 10000);
      if (f > 1) return err('ppm', 'Your bleach is weaker than the strength you want. Check both numbers.');
    } else if (k === 'hectare') {
      var r = fieldNum(rate.amount, rate.unit === 'l' ? 'l' : 'ml');
      var out = fieldNum(rate.output, 'out');
      if (!pos(r)) return err('amount', notNumber(rate.amount) && !blank(rate.amount) ? 'Type the label rate as a number only, for example 2.' : 'Enter the label rate per hectare.');
      if (!pos(out)) return err('output', 'Enter how many litres of mix your sprayer puts out per hectare. Use the quick test if you do not know.');
      var rMl = rate.unit === 'l' ? r * 1000 : r;
      f = rMl / (out * 1000);
      if (f > 1) return err('amount', 'The label rate is more than your sprayer puts out. Check the rate and the output.');
    } else {
      return { ok: false, error: 'Unknown rate type.' };
    }
    if (!(f > 0) || !isFinite(f)) return { ok: false, error: 'Check the numbers.' };
    return { ok: true, f: f };
  }

  // Rounds an amount of concentrate up to the step it is shown in, so the calculator never shows less than the maths
  // (DIL-10). The water is the rest, so the two still add up to the mix.
  function upMl(ml) {
    if (!(ml > 0) || !isFinite(ml)) return ml;
    var step = ml < 0.1 ? 0.001 : ml < 2 ? 0.01 : ml < 10 ? 0.1 : ml < 100 ? 0.5 : ml < 10000 ? 1 : ml < 100000 ? 10 : 100;
    // Rounded so 0.1 + 0.2 style float error never shows as 0.30000000000000004.
    return Math.round(Math.ceil(ml / step - 1e-6) * step * 1e6) / 1e6;
  }

  // The full answer for a rate and a finished volume in litres.
  // opts.strength: strength of the concentrate in per cent (optional, gives the final strength).
  function mix(rate, litres, opts) {
    opts = opts || {};
    var fr = fraction(rate);
    if (!fr.ok) return fr;
    var L = fieldNum(litres, 'l');
    if (!pos(L)) return err('litres', notNumber(litres) && !blank(litres) ? 'Type the litres as a number only, for example 5.' : rate.kind === 'hectare' ? 'Enter the tank size in litres.' : 'Enter how much mix you want to make, in litres.');
    var vol = L * 1000;
    var f = fr.f;
    var conc = vol * f;
    var shown = Math.min(upMl(conc), vol);
    var res = {
      ok: true,
      f: f,
      volumeMl: vol,
      concMl: conc,
      waterMl: vol - conc,
      // As shown: the concentrate rounded up, and the water is the rest.
      concShownMl: shown,
      waterShownMl: Math.max(vol - shown, 0),
      ratioN: f >= 1 ? 0 : (1 - f) / f,
      oneIn: 1 / f,
      mlPerL: f * 1000,
      mlPerLWater: f >= 1 ? null : (f / (1 - f)) * 1000,
      pct: f * 100,
      neat: f >= 1,
      finalPct: null,
      finalPpm: null,
      notes: [],
    };
    if (rate.kind === 'hectare') {
      var out = fieldNum(rate.output, 'out');
      res.output = out;
      res.coverHa = L / out;
      if (out > OUTPUT_HIGH) res.notes.push({ id: 'output-high', text: fmtNum(out, 0) + ' L per hectare is far more than a hand, knapsack or small boom sprayer puts out (about 100 to 500 L per hectare). Check the area in your test was in square metres, not hectares.' });
    }
    var s = rate.kind === 'ppm' ? fieldNum(rate.strength, 'pct') : fieldNum(opts.strength, 'pct');
    if (pos(s) && s <= 100) {
      res.finalPct = s * f;
      res.finalPpm = s * f * 10000;
      res.strength = s;
    }
    return res;
  }

  // Calibration: litres sprayed over an area in square metres gives litres per hectare.
  function outputPerHa(litresUsed, areaM2) {
    var l = fieldNum(litresUsed, 'l');
    var a = fieldNum(areaM2, 'm2');
    if (!pos(l) || !pos(a)) return null;
    return (l / a) * 10000;
  }

  // An area typed in m² or hectares (DIL-06). Returns m², or NaN.
  function areaM2(x, unit) {
    var v = fieldNum(x, unit === 'ha' ? 'ha' : 'm2');
    return unit === 'ha' ? v * 10000 : v;
  }
  // A job by area for the per hectare mode. Returns spray volume, tank fills and total product. areaUnit: 'm2' or 'ha'.
  function job(rate, tankLitres, area, areaUnit) {
    var fr = fraction(rate);
    var a = areaM2(area, areaUnit);
    var t = fieldNum(tankLitres, 'l');
    if (!fr.ok || rate.kind !== 'hectare' || !pos(a) || !pos(t)) return null;
    var sprayL = fieldNum(rate.output, 'out') * a / 10000;
    var amt = fieldNum(rate.amount, rate.unit === 'l' ? 'l' : 'ml');
    var productMl = upMl((rate.unit === 'l' ? amt * 1000 : amt) * a / 10000);
    return { areaM2: a, sprayL: sprayL, tanks: Math.ceil(sprayL / t - 1e-9), fullTanks: sprayL / t, productMl: productMl };
  }

  // ---------- Formatting (Australian English, metric) ----------

  function group(intStr) { return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  // Round to d decimals and drop trailing zeros.
  function trim(x, d) {
    var s = x.toFixed(d);
    if (s.indexOf('.') > -1) s = s.replace(/0+$/, '').replace(/\.$/, '');
    var bits = s.split('.');
    return group(bits[0]) + (bits[1] ? '.' + bits[1] : '');
  }

  // Millilitres for people: 0.45 mL, 1.24 mL, 9.1 mL, 12.5 mL, 91 mL, 762 mL, 1.455 L, 45.5 L.
  // From 10 to 100 mL it shows the nearest half millilitre, which a measuring cup or syringe can show; up to 10 L
  // it shows whole millilitres. Concentrate is rounded up first (upMl), so it never shows less than the maths.
  function fmtMl(ml) {
    if (!isFinite(ml)) return '';
    var a = Math.abs(ml);
    if (a === 0) return '0 mL';
    if (a < 0.001) return 'less than 0.001 mL';
    if (a < 0.1) return trim(ml, 3) + ' mL';
    if (a < 2) return trim(ml, 2) + ' mL';
    if (a < 10) return trim(ml, 1) + ' mL';
    if (a < 99.75) return trim(Math.round(ml * 2) / 2, 1) + ' mL';
    if (a < 999.5) return trim(Math.round(ml), 0) + ' mL';
    var l = ml / 1000;
    if (Math.abs(l) < 10) return trim(l, 3) + ' L';
    if (Math.abs(l) < 100) return trim(l, 2) + ' L';
    return trim(l, 1) + ' L';
  }
  // Concentrate or product: rounded up to the step shown.
  function fmtConc(ml) { return fmtMl(upMl(ml)); }
  // Water, the rest of the mix after the concentrate as shown. It is shown exactly, so the two add up to the mix
  // (DIL-R1): 2.5 mL of concentrate and 997.5 mL of water make 1 L (it used to round the water to 998 mL).
  function fmtWater(ml) {
    if (!isFinite(ml)) return '';
    var w = Math.round(ml * 1000) / 1000;
    return w >= 1000 ? trim(w / 1000, 6) + ' L' : trim(w, 3) + ' mL';
  }

  function fmtL(l) { return fmtMl(l * 1000); }

  function fmtPct(p) {
    if (!isFinite(p)) return '';
    if (p >= 10) return trim(p, 1) + '%';
    if (p >= 1) return trim(p, 2) + '%';
    if (p >= 0.1) return trim(p, 3) + '%';
    return trim(p, 4) + '%';
  }

  function fmtPpm(ppm) {
    if (!isFinite(ppm)) return '';
    if (ppm >= 100) return trim(Math.round(ppm), 0) + ' ppm';
    return trim(ppm, 1) + ' ppm';
  }

  // 1:10, 1:9.5, 1:200, 1:0.25. Never 1:0 for a ratio that is not neat (DIL-13).
  function fmtRatio(n) {
    if (!isFinite(n)) return '';
    if (n >= 20) return '1:' + trim(Math.round(n), 0);
    if (n >= 1 || n === 0) return '1:' + trim(n, 1);
    return '1:' + typed(n);
  }

  function fmtNum(x, d) { return isFinite(x) ? trim(x, d === undefined ? 2 : d) : ''; }
  // A number the visitor typed, shown back as typed (up to 4 places), never as 0 when it is not 0.
  function typed(x) {
    if (!isFinite(x)) return '';
    if (x !== 0 && Math.abs(x) < 0.0001) return trim(x, 8);
    return trim(x, 4);
  }

  // Strength of an active in the mix: ppm under 1%, per cent from 1% up.
  function fmtStrength(pct) {
    if (!isFinite(pct)) return '';
    return pct < 1 ? fmtPpm(pct * 10000) : fmtPct(pct);
  }

  // The label rate in words, for the result and the quote note (DIL-16).
  function describe(rate) {
    if (!rate) return '';
    var k = rate.kind;
    if (k === 'ratio') {
      var n = ratioN(rate.n);
      if (n === 0) return 'Neat, no water (1:0)';
      return '1 part concentrate to ' + typed(n) + ' part' + (n === 1 ? '' : 's') + ' water (' + fmtRatio(n) + ')';
    }
    if (k === 'onein') { var m = oneInN(rate.n); return m === 1 ? 'Neat, no water (1 in 1)' : '1 part in ' + typed(m) + ' parts of mix'; }
    if (k === 'per') { var pl = fieldNum(rate.perL, 'l'); return typed(fieldNum(rate.amount, rate.unit === 'l' ? 'l' : 'ml')) + ' ' + (rate.unit === 'l' ? 'L' : 'mL') + ' per ' + (pl === 1 ? 'litre' : typed(pl) + ' L') + ', made up with water'; }
    if (k === 'percent') return typed(fieldNum(rate.pct, 'pct')) + '% of the mix';
    if (k === 'ppm') return typed(fieldNum(rate.ppm, 'ppm')) + ' ppm from ' + typed(fieldNum(rate.strength, 'pct')) + '% bleach';
    if (k === 'hectare') return typed(fieldNum(rate.amount, rate.unit === 'l' ? 'l' : 'ml')) + ' ' + (rate.unit === 'l' ? 'L' : 'mL') + ' per hectare, sprayer output ' + fmtNum(fieldNum(rate.output, 'out'), 0) + ' L per hectare';
    return '';
  }

  return {
    SIZES: SIZES, TANKS: TANKS, PRESETS: PRESETS, OUTPUT_HIGH: OUTPUT_HIGH,
    num: num, fieldNum: fieldNum, ratioN: ratioN, oneInN: oneInN, fraction: fraction, mix: mix, outputPerHa: outputPerHa, areaM2: areaM2, job: job, upMl: upMl,
    fmtMl: fmtMl, fmtConc: fmtConc, fmtWater: fmtWater, fmtL: fmtL, fmtPct: fmtPct, fmtPpm: fmtPpm, fmtRatio: fmtRatio, fmtNum: fmtNum, typed: typed, fmtStrength: fmtStrength, describe: describe,
  };
});

/*
 * NORA PRO Dilution Calculator (page UI).
 * Built into assets/nora-mix.js after tools/chem-guide/engine.js and mix-engine.js by tools/mix-calc/build.mjs.
 * The maths lives in mix-engine.js. The strength check uses the chemical guide engine and dataset
 * (assets/nora-chemicals.json), loaded only when a chemical is needed, so the page stays light.
 * No key and no network call except the dataset file.
 */
(function () {
  'use strict';
  var root = document.getElementById('nora-mix');
  if (!root || typeof NoraMix === 'undefined') return;
  var M = NoraMix;
  var E = typeof NoraChem !== 'undefined' ? NoraChem : null;

  var jsonUrl = root.getAttribute('data-json');
  var quoteBase = root.getAttribute('data-quote-url') || '/pages/request-a-quote';
  var guideUrl = root.getAttribute('data-guide-url') || '/pages/chemical-compatibility-guide';

  function $(sel) { return root.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  // Writes the answer bar's text only when it changes. Rewriting it on every focus change replaced the element under
  // the pointer between mouse down and mouse up, so the browser dropped the click (ALL-R1).
  function setHtml(el, h) { if (el && el.__h !== h) { el.innerHTML = h; el.__h = h; } }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function val(id) { var x = $('#' + id); return x ? x.value : ''; }
  function setVal(id, v) { var x = $('#' + id); if (x) x.value = v; }

  var state = {
    mode: 'rate', chem: null, chemAuto: false, data: null, loading: null, used: false, toolUse: false, edited: false,
    lastSig: '', timer: null, linkTimer: null, touched: {}, errs: [], cleared: {}, strengthFor: null, userChem: null, last: null,
  };

  // ---------------------------------------------------------------- analytics (same GTM tag as the guide)

  // GTM merges each push into one data model, so guide_via and guide_restored are sent on every event (empty when
  // they do not apply) and a later event never carries the value from an earlier one.
  function track(event, params) {
    try {
      var payload = { event: event, guide_mode: 'mix', guide_via: undefined, guide_restored: undefined };
      Object.keys(params || {}).forEach(function (k) { if (params[k] !== undefined && params[k] !== null && params[k] !== '') payload[k] = params[k]; });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* never break the tool for analytics */ }
  }
  // guide_tool_use fires on the first edit, also when the mix came from a share link.
  function firstUse() {
    state.used = true;
    state.edited = true;
    if (state.toolUse) return;
    state.toolUse = true;
    track('guide_tool_use', { guide_kind: state.mode });
  }

  // ---------------------------------------------------------------- dataset (lazy)

  function load() {
    if (state.data) return Promise.resolve(state.data);
    if (state.loading) return state.loading;
    if (!E || !jsonUrl || !window.fetch) return Promise.resolve(null);
    // Same request as the guide tool: a plain fetch, which keeps the site's own sign-in on a preview host.
    state.loading = fetch(jsonUrl)
      .then(function (r) { if (!r.ok) throw new Error('dataset ' + r.status); return r.json(); })
      .then(function (d) { state.data = d; return d; })
      .catch(function () { state.loading = null; return null; });
    return state.loading;
  }

  // A different chemical starts with an empty strength box, so a strength typed for one chemical is never used for
  // the next (DIL-01). keep: the strength came with this chemical (typed in the search, or from a share link).
  function adopt(row, auto, keep) {
    var before = state.chem ? state.chem.id : null;
    if (!keep && row && val('nm-strength').trim() !== '' && state.strengthFor && state.strengthFor !== row.id && before !== row.id) setVal('nm-strength', '');
    state.chem = row;
    state.chemAuto = !!auto;
    if (row) state.strengthFor = row.id;
  }

  // auto: picked by the tool (bleach in the sanitiser tab), not by the visitor, so it is dropped when the tab changes.
  function setChem(id, auto, keep) {
    if (!id) {
      state.chem = null; state.chemAuto = false; state.strengthFor = null;
      setVal('nm-chem', ''); setVal('nm-strength', ''); closeList(); render(); return Promise.resolve();
    }
    return load().then(function (d) {
      if (!d) return;
      var row = E.byId(d, id);
      if (!row) return;
      adopt(row, auto, keep);
      setVal('nm-chem', row.name);
      closeList();
      render();
    });
  }

  // ---------------------------------------------------------------- reading the form

  function kind() {
    var r = $('input[name="nm-kind"]:checked');
    return r ? r.value : 'ratio';
  }

  function currentRate() {
    if (state.mode === 'ppm') return { kind: 'ppm', ppm: val('nm-ppm'), strength: val('nm-cs') };
    if (state.mode === 'hectare') return { kind: 'hectare', amount: val('nm-ha-amt'), unit: val('nm-ha-unit'), output: val('nm-out') };
    var k = kind();
    if (k === 'ratio') return { kind: 'ratio', n: val('nm-n') };
    if (k === 'onein') return { kind: 'onein', n: val('nm-onein') };
    if (k === 'per') return { kind: 'per', amount: val('nm-amt'), unit: val('nm-unit'), perL: val('nm-perl') };
    return { kind: 'percent', pct: val('nm-pct') };
  }

  // ---------------------------------------------------------------- view

  function bar(f) {
    var w = Math.max(f * 100, 0.6);
    if (f >= 1) w = 100;
    return '<div class="nm-bar" aria-hidden="true"><span class="nm-bar__c" style="width:' + w.toFixed(2) + '%"></span></div>' +
      '<p class="nm-key" aria-hidden="true"><span class="nm-key__c"></span>Concentrate <span class="nm-key__w"></span>Water</p>';
  }

  // Plain words for the guide's ratings and seals (DIL-09). The guide link has the full answer and its sources.
  var RATING = { suitable: 'Suitable', conditional: 'Works with conditions', ask: 'Ask us' };
  function sealWords(seal) { return 'Rubber seals: ' + seal + (/ or /.test(seal) ? '' : ' type') + '.'; }

  function checkBlock(res) {
    if (!state.chem || !state.data) return { html: '', r: null };
    var row = state.chem;
    var strength = res.finalPct;
    var usable = row.scale === 'pct' && strength !== null && strength !== undefined;
    var r = E.resolve(state.data, row, { strength: usable ? strength : null });
    var badge = '<span class="nm-badge nm-badge--' + esc(r.rating) + '">' + esc(RATING[r.rating] || r.rating) + '</span>';
    var what;
    if (r.text) what = r.text;
    else if (r.famLabels.length) what = 'The ' + r.famLabels.join(' or the ').toLowerCase() + '.' + (r.seal ? ' ' + sealWords(r.seal) : '');
    else what = r.note || 'Our sources do not rate a sprayer for this. Ask us with the SDS.';
    // What the search matched, so "metho" or "glyphosate" shows which guide entry was checked (DIL-08).
    var typedName = val('nm-chem').trim();
    var matched = '<p class="nm-small nm-matched"><b>Checked as:</b> ' + esc(row.name) +
      (typedName && !state.chemAuto && typedName.toLowerCase() !== row.name.toLowerCase() ? ' <span class="nm-opt">(you typed “' + esc(typedName.slice(0, 40)) + '”)</span>' : '') + '</p>';
    var note = r.note && r.note !== what ? '<p class="nm-small nm-note">' + esc(r.note) + '</p>' : '';
    var strengthLine = '';
    if (usable) strengthLine = '<p class="nm-small">Checked at the strength in the sprayer: ' + esc(M.fmtStrength(strength)) + ' ' + esc(row.name.toLowerCase()) + '.</p>';
    else if (r.needsStrength) strengthLine = '<p class="nm-small">Add the strength of the ' + esc(row.name.toLowerCase()) + ' (from its label) to check the strength in the sprayer.</p>';
    var models = '';
    if (r.fams.length) {
      var groups = E.modelsFor(state.data, r.fams);
      var links = [];
      groups.forEach(function (g) {
        g.rated.concat(g.other).forEach(function (m) {
          links.push('<a class="nm-model" href="/products/' + esc(m.handle) + '">' + esc((m.brand === 'Klager' ? '' : m.brand + ' ') + m.name) + '</a>');
        });
      });
      if (links.length) models = '<p class="nm-small"><b>Models we stock:</b> ' + links.join(', ') + '</p>';
    }
    var scale = usable && strength >= 1 ? 100 : 10000;
    var guideLink = guideUrl + '#guide&c=' + encodeURIComponent(row.id + (usable ? ':' + (Math.round(strength * scale) / scale) : '')) + '&m=one';
    var html = '<div class="nm-check-out">' +
      '<div class="nm-check-out__top"><p class="nm-check-out__h">Can your sprayer take it?</p>' + badge + '</div>' + matched +
      '<p class="nm-check-out__what">' + esc(what) + '</p>' + note + strengthLine + models +
      '<p class="nm-small"><a class="nm-link" href="' + esc(guideLink) + '">See the full answer and its sources in the Chemical Compatibility Guide</a></p>' +
      '</div>';
    return { html: html, r: r };
  }

  function safetyLines() {
    var out = [];
    var g = state.chem ? state.chem.group : '';
    var acid = g === 'acids' || /acid/i.test(state.chem ? state.chem.name : '');
    if (acid) out.push('Add acid to water, never water to acid. Mix in a plastic container and wear the protection the SDS lists.');
    if (state.mode === 'ppm' || g === 'bleach') out.push('Never mix bleach with acids or ammonia. Make sanitiser fresh each day and throw out what is left (FSANZ).');
    if (state.mode === 'hectare') out.push('Use the rate on the label for your weed, crop and situation. This calculator only does the maths.');
    out.push('Follow the product label and SDS. Label the sprayer unless you use it all straight away yourself.');
    return '<ul class="nm-safety">' + out.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>';
  }

  function chemLine(res) {
    if (!state.chem) return '';
    return state.chem.name + (res.finalPct !== null ? ', ' + M.fmtStrength(res.finalPct) + ' in the sprayer' : '');
  }

  function quoteUrl(res, chk) {
    var rate = currentRate();
    var size = M.fmtL(res.volumeMl / 1000);
    var note = 'From the NORA PRO dilution calculator: ' + M.describe(rate) + '. ' + M.fmtConc(res.concMl) + ' concentrate and ' +
      M.fmtWater(res.waterShownMl) + ' water makes ' + size + '.';
    var chem = '';
    if (state.chem) {
      chem = state.chem.name + (res.finalPct !== null ? ' (' + M.fmtStrength(res.finalPct) + ' in the sprayer)' : '');
      note += ' Chemical: ' + chem + '.';
    }
    var q = [];
    if (chk && chk.r && chk.r.famLabels.length) q.push('recommended=' + encodeURIComponent(chk.r.famLabels.join(' or ')));
    if (chem) q.push('chemical=' + encodeURIComponent(chem));
    if (chk && chk.r && chk.r.seal && chk.r.fams.length) q.push('seal=' + encodeURIComponent(chk.r.seal));
    q.push('note=' + encodeURIComponent(note));
    return quoteBase + '?' + q.join('&');
  }

  // ---------------------------------------------------------------- marking boxes and their messages (ALL-01)

  // The engine names the box at fault; these are the ids on the page for each tab.
  function fieldId(field) {
    if (field === 'litres') return 'nm-litres';
    if (state.mode === 'ppm') return { ppm: 'nm-ppm', strength: 'nm-cs' }[field];
    if (state.mode === 'hectare') return { amount: 'nm-ha-amt', output: 'nm-out' }[field];
    return { n: kind() === 'onein' ? 'nm-onein' : 'nm-n', amount: 'nm-amt', perL: 'nm-perl', pct: 'nm-pct' }[field];
  }
  // Every box the tab shows, with a rule and a message, so all the boxes that cannot work are marked at once.
  function fieldRules() {
    var pos = function (u) { return function (v) { var n = M.fieldNum(v, u); return n > 0 && isFinite(n); }; };
    var pct = function (v) { var n = M.fieldNum(v, 'pct'); return n > 0 && n <= 100; };
    var rules = [['nm-litres', pos('l'), state.mode === 'hectare' ? 'Enter the tank size in litres, for example 15.' : 'Enter how much mix you are making, in litres, for example 5.']];
    if (state.mode === 'ppm') {
      rules.push(['nm-ppm', pos('ppm'), 'Enter the strength you want in ppm, for example 100.'], ['nm-cs', pct, 'Pick how strong your bleach is, or type the per cent from its label.']);
    } else if (state.mode === 'hectare') {
      rules.push(['nm-ha-amt', pos(val('nm-ha-unit')), 'Enter the label rate per hectare, for example 2.'], ['nm-out', pos('out'), 'Enter the litres per hectare your sprayer puts out, for example 200.']);
      rules.push(['nm-area', function (v) { return v.trim() === '' || M.areaM2(v, val('nm-area-unit')) > 0; }, 'Enter the area as a number above 0, or leave it empty.']);
    } else {
      var k = kind();
      if (k === 'ratio') rules.push(['nm-n', function (v) { var n = M.ratioN(v); return n >= 0 && isFinite(n) && !/^\s*1\s*in\b/i.test(v); }, 'Enter the number of parts water, for example 20 for 1:20.']);
      if (k === 'onein') rules.push(['nm-onein', function (v) { return M.oneInN(v) >= 1 && !/^\s*1\s*:/.test(v); }, 'Enter the total parts, for example 40 for 1 in 40.']);
      if (k === 'per') rules.push(['nm-amt', pos(val('nm-unit')), 'Enter the amount from the label, for example 20.'], ['nm-perl', pos('l'), 'Enter the litres the amount is for, for example 1.']);
      if (k === 'percent') rules.push(['nm-pct', pct, 'Enter a per cent between 0 and 100, for example 2.']);
    }
    if (state.mode !== 'ppm') rules.push(['nm-strength', function (v) { return v.trim() === '' || pct(v); }, 'The strength must be a per cent above 0 and up to 100, so it is left out.']);
    return rules;
  }
  function errLine(id) { var el = $('#' + id); var row = el ? el.closest('.nm-row') : null; return row ? $('#' + row.getAttribute('data-err')) : null; }
  // A box is marked only once it has a value that cannot work, or after the visitor has left it empty. The words that
  // explain it go just under its row (aria-describedby), and the status line and the answer bar say them too.
  function markFields(res) {
    $$('.nm-in[aria-invalid]').forEach(function (x) { x.removeAttribute('aria-invalid'); x.removeAttribute('aria-describedby'); });
    $$('.nm-err').forEach(function (p) { p.hidden = true; p.textContent = ''; });
    state.errs = [];
    var engineId = res && !res.ok && res.field ? fieldId(res.field) : null;
    fieldRules().forEach(function (r) {
      var el = $('#' + r[0]);
      if (!el || el.offsetParent === null) return;
      var v = el.value;
      if (v.trim() === '' && !state.touched[r[0]]) return;
      var bad = !r[1](v) || r[0] === engineId;
      if (!bad) return;
      var msg = r[0] === engineId ? res.error : r[2];
      if (r[0] === 'nm-amt' && state.cleared['nm-amt'] && v.trim() === '') msg = 'You changed the unit, so type the amount again in ' + (val('nm-unit') === 'l' ? 'L' : 'mL') + '.';
      el.setAttribute('aria-invalid', 'true');
      var line = errLine(r[0]);
      if (line) {
        el.setAttribute('aria-describedby', line.id);
        if (line.hidden) { line.hidden = false; line.textContent = msg; }
      }
      state.errs.push({ id: r[0], error: msg });
    });
  }

  function render() {
    var out = $('#nm-result');
    var rate = currentRate();
    var litres = val('nm-litres');
    var opts = { strength: state.mode === 'ppm' ? null : val('nm-strength') };
    var res = M.mix(rate, litres, opts);
    markFields(res);
    syncChemUi();
    syncChips();
    if (!res.ok) {
      var msg = state.cleared['nm-amt'] && rate.kind === 'per' && val('nm-amt').trim() === '' ? 'You changed the unit, so type the amount again in ' + (val('nm-unit') === 'l' ? 'L' : 'mL') + '.' : res.error;
      out.innerHTML = '<div class="nm-card nm-card--wait" tabindex="-1"><p class="nm-wait">' + esc(msg) + '</p></div>';
      $('#nm-check').innerHTML = '';
      state.last = null;
      peek();
      schedule(res, null);
      saveLink();
      return;
    }
    var size = M.fmtL(res.volumeMl / 1000);
    var conc = M.fmtConc(res.concMl);
    var water = M.fmtWater(res.waterShownMl);
    var head;
    if (res.neat) head = 'Use the concentrate neat. No water.';
    else head = 'Add <b>' + esc(conc) + '</b> of concentrate to <b>' + esc(water) + '</b> of water';
    var eq = [
      ['Ratio', res.neat ? 'neat' : M.fmtRatio(res.ratioN)],
      ['Same as', res.neat ? 'neat' : '1 in ' + M.fmtNum(res.oneIn, 1)],
      ['Per litre of mix', M.fmtConc(res.mlPerL)],
      ['Per cent', M.fmtPct(res.pct)],
    ];
    if (res.finalPct !== null) eq.push(['In the sprayer', M.fmtStrength(res.finalPct)]);

    var jobHtml = '';
    var notes = res.notes.slice();
    if (state.mode === 'hectare') {
      // One tank covers so many hectares (DIL-06); with an area, the spray, fills and product for the job.
      var cover = '<p>One ' + esc(size) + ' tank covers ' + esc(M.fmtNum(res.coverHa, res.coverHa < 1 ? 3 : 2)) + ' ha (' + esc(M.fmtNum(res.coverHa * 10000, 0)) + ' m&sup2;).</p>';
      var j = M.job(rate, litres, val('nm-area'), val('nm-area-unit'));
      var area = '';
      if (j) {
        var areaText = val('nm-area-unit') === 'ha' ? M.typed(j.areaM2 / 10000) + ' ha' : M.fmtNum(j.areaM2, 0) + ' m²';
        area = '<p class="nm-job__h">For ' + esc(areaText) + '</p><p>' + esc(M.fmtL(j.sprayL)) + ' of mix, ' + j.tanks + ' fill' + (j.tanks === 1 ? '' : 's') + ' of a ' + esc(size) +
          ' tank, ' + esc(M.fmtMl(j.productMl)) + ' of product in total.</p>';
        state.jobText = 'For ' + areaText + ': ' + M.fmtL(j.sprayL) + ' of mix, ' + j.tanks + ' tank fill' + (j.tanks === 1 ? '' : 's') + ', ' + M.fmtMl(j.productMl) + ' of product';
      } else state.jobText = '';
      jobHtml = '<div class="nm-job">' + cover + area + '</div>';
    } else state.jobText = '';

    var chk = checkBlock(res);
    var badStrength = '';
    if (state.mode !== 'ppm' && val('nm-strength').trim() !== '' && res.finalPct === null) {
      badStrength = '<p class="nm-small nm-warn">The strength of the concentrate must be a per cent above 0 and up to 100, so it is left out.</p>';
    }
    var qUrl = quoteUrl(res, chk);
    var made = rate.kind === 'per' && !res.neat ? '<p class="nm-small">Made up to volume, as labels say: put the ' + esc(conc) + ' of concentrate in, then fill with water to ' + esc(size) + '.</p>' : '';
    var html = '<div class="nm-card" tabindex="-1" aria-labelledby="nm-answer">' +
      '<p class="nm-eyebrow">For ' + esc(size) + ' of mix</p>' +
      '<p class="nm-answer" id="nm-answer">' + head + '</p>' +
      (res.neat ? '' : bar(res.f)) +
      '<p class="nm-rate">' + esc(M.describe(rate)) + '.</p>' +
      (state.chem ? '<p class="nm-rate nm-rate--chem">Chemical: ' + esc(chemLine(res)) + '.</p>' : '') +
      '<dl class="nm-eq">' + eq.map(function (p) { return '<div><dt>' + esc(p[0]) + '</dt><dd>' + esc(p[1]) + '</dd></div>'; }).join('') + '</dl>' +
      made + jobHtml +
      (notes.length ? '<ul class="nm-notes">' + notes.map(function (n) { return '<li><b>Check:</b> ' + esc(n.text) + '</li>'; }).join('') + '</ul>' : '') +
      safetyLines() +
      '<p class="nm-small nm-round">Concentrate is rounded up, so you never use less than the label rate.</p>' +
      '<div class="nm-actions">' +
      '<button type="button" class="nm-btn" data-nm-copy>Copy the mix</button>' +
      '<button type="button" class="nm-act" data-nm-link>Copy link</button>' +
      '<button type="button" class="nm-act" data-nm-print>Print</button>' +
      '<span class="nm-copied" role="status" aria-live="polite"></span>' +
      '</div></div>';
    out.innerHTML = html;
    var chkHtml = badStrength + chk.html;
    if (chk.r) {
      chkHtml += '<div class="nm-quote"><a class="nm-btn" data-nm-quote href="' + esc(qUrl) + '">' +
        (chk.r.fams.length ? 'Request a quote for this sprayer' : 'Ask us to confirm a sprayer') + '</a>' +
        '<p class="nm-small">Your chemical and mix go into the form for you.</p></div>';
    }
    $('#nm-check').innerHTML = chkHtml;
    state.last = { res: res, rate: rate, chk: chk, size: size, conc: conc, water: water };
    stickyTop();
    peek();
    schedule(res, chk);
    saveLink();
  }

  // The strength box only matters once a chemical is chosen, and its label names the chemical (DIL-01).
  // Clear only shows when there is something to clear.
  function syncChemUi() {
    var hasChem = !!state.chem || val('nm-strength').trim() !== '';
    $('#nm-strength-row').hidden = state.mode === 'ppm' || !hasChem;
    var lab = $('#nm-strength-label');
    if (lab) {
      var n = state.chem ? state.chem.name.toLowerCase() : '';
      lab.textContent = n && n.length <= 30 ? 'Strength of the ' + n + ', from its label' : 'Strength of the concentrate, from its label';
    }
    var clear = $('[data-nm-clear]');
    if (clear) clear.hidden = val('nm-chem') === '' && !state.chem;
  }
  // Quick pick buttons show as chosen when their value is in the box (bleach strengths, ppm, sizes).
  function syncChips() {
    var v = M.fieldNum(val('nm-litres'), 'l');
    $$('[data-nm-size]').forEach(function (b) { b.setAttribute('aria-pressed', M.num(b.getAttribute('data-nm-size')) === v ? 'true' : 'false'); });
    var cs = M.fieldNum(val('nm-cs'), 'pct');
    $$('[data-nm-cs]').forEach(function (b) { b.setAttribute('aria-pressed', M.num(b.getAttribute('data-nm-cs')) === cs ? 'true' : 'false'); });
    var ppm = M.fieldNum(val('nm-ppm'), 'ppm');
    $$('[data-nm-ppm]').forEach(function (b) { b.setAttribute('aria-pressed', M.num(b.getAttribute('data-nm-ppm')) === ppm ? 'true' : 'false'); });
  }

  // ---------------------------------------------------------------- answer bar

  // Shown whenever the calculator is on screen but its answer line is not (under the site header, or below the fold).
  // Phones and tablets get a bar along the bottom; desktop gets a small box in the corner. The answer line counts as
  // on screen once a little of it shows above the bottom edge, so the bar never sits on a visible answer (DIL-18).
  // Where the site header ends right now: 0 once it has slid away on scroll (ALL-R2). The height alone counted a
  // hidden header, so an answer at the top of the screen was taken as covered.
  function headerBottom() {
    var b = 0;
    try {
      document.querySelectorAll('.shopify-section-header-sticky, sticky-header, .section-header').forEach(function (el) {
        var cs = getComputedStyle(el);
        if (cs.position === 'sticky' || cs.position === 'fixed') b = Math.max(b, el.getBoundingClientRect().bottom);
      });
    } catch (e) { b = state.headerH || 0; }
    return Math.max(0, Math.min(b, 160));
  }
  function answerInView() {
    var a = $('#nm-answer') || $('#nm-result .nm-card');
    if (!a) return true;
    var r = a.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    // At the top, at least half the line must show below the header, so the amounts are readable (ALL-R2).
    return (r.top + r.bottom) / 2 > headerBottom() && r.top < vh - 24;
  }
  function toolInView() {
    var r = root.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 80 && r.top < vh - 80;
  }
  // The bar (phones) or corner box (desktop) would sit on the card's buttons: it stays hidden until they move clear.
  function coversActions(bar) {
    var a = $('#nm-result .nm-actions');
    if (!a) return false;
    var r = a.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var vw = document.documentElement.clientWidth || window.innerWidth;
    var cs = getComputedStyle(bar);
    var bottom = parseFloat(cs.bottom) || 0;
    var right = parseFloat(cs.right) || 0;
    return r.height > 0 && r.bottom > vh - bottom - bar.offsetHeight && r.top < vh - bottom && r.right > vw - right - bar.offsetWidth;
  }
  // Typing in a box on a short screen (the phone keyboard is up): the bar would cover the box.
  function typingOnShortScreen() {
    var a = document.activeElement;
    if (!a || !root.contains(a) || !/^(INPUT|SELECT)$/.test(a.tagName)) return false;
    var h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    return h < 450;
  }
  function peek() {
    var bar = $('#nm-peek');
    if (!bar) return;
    var L = state.last;
    var err = !L && state.errs.length ? state.errs[0] : null;
    var show = (!!L || !!err) && toolInView() && !answerInView() && !typingOnShortScreen();
    if (L) {
      // The button's name is its own text, so what is read out matches what is shown.
      setHtml($('#nm-peek-txt'), (L.res.neat ? 'Use the concentrate neat' :
        '<b>' + esc(L.conc) + '</b> concentrate<span class="nm-peek__more"> + <b>' + esc(L.water) + '</b> water</span>') +
        '<span class="nm-status">. Show the full answer.</span>');
    } else if (err) {
      setHtml($('#nm-peek-txt'), '<span class="nm-peek__err">Check: ' + esc(err.error) + '</span><span class="nm-status">. Go to the box.</span>');
    }
    if (show && coversActions(bar)) show = false;
    bar.setAttribute('data-show', show ? '1' : '0');
    bar.setAttribute('data-err', err ? '1' : '0');
    bar.tabIndex = show ? 0 : -1;
    var wide = window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
    try { document.documentElement.style.scrollPaddingBottom = show && !wide ? '84px' : ''; } catch (e) { /* ignore */ }
  }
  function watchPeek() {
    var bar = $('#nm-peek');
    if (!bar) return;
    bar.hidden = false;
    var ticking = false;
    var onMove = function () {
      if (ticking) return;
      ticking = true;
      (window.requestAnimationFrame || setTimeout)(function () { ticking = false; peek(); });
    };
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', onMove);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', onMove);
    // The page settles after load and once the fonts arrive, so the bar is checked again then (ALL-R2).
    window.addEventListener('load', onMove);
    try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(onMove); } catch (e) { /* ignore */ }
    peek();
  }

  // Desktop: the answer card stays in view just under the site's sticky header. A card taller than the screen
  // sticks by its bottom instead, so its buttons stay in reach (the corner answer box shows the amounts meanwhile).
  function stickyTop() {
    try {
      var h = 0;
      document.querySelectorAll('.shopify-section-header-sticky, sticky-header, .section-header').forEach(function (el) {
        var cs = getComputedStyle(el);
        if (cs.position === 'sticky' || cs.position === 'fixed') h = Math.max(h, el.getBoundingClientRect().height);
      });
      state.headerH = Math.round(Math.min(h, 160));
      var res = $('#nm-result');
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var top = state.headerH + 20;
      if (res) top = Math.min(top, vh - res.getBoundingClientRect().height - 16);
      root.style.setProperty('--nm-top', Math.round(top) + 'px');
    } catch (e) { /* the CSS default is used */ }
  }

  // Count a result once the visitor stops typing, not on every key. The signature includes the strength, so a
  // strength that changes the check is a new result (DIL-12). Screen readers hear the result, or the box to check.
  function schedule(res, chk) {
    if (!state.used) return;
    clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      var sig = JSON.stringify([state.mode, currentRate(), val('nm-litres'), state.chem ? state.chem.id : '', val('nm-strength'), val('nm-area'), val('nm-area-unit')]);
      if (sig === state.lastSig) return;
      state.lastSig = sig;
      var s = $('#nm-status');
      if (state.errs.length) { if (s) s.textContent = 'Check: ' + state.errs[0].error; return; }
      if (!res.ok) { if (s) s.textContent = ''; return; }
      var p = { guide_kind: state.mode };
      if (state.chem) { p.guide_chemical = state.chem.id; if (chk && chk.r) p.guide_rating = chk.r.rating; }
      if (res.finalPct !== null) p.guide_strength_given = 1;
      track('guide_result_shown', p);
      if (s) s.textContent = 'Result updated: ' + (res.neat ? 'use neat' : M.fmtConc(res.concMl) + ' concentrate, ' + M.fmtWater(res.waterShownMl) + ' water') + '.';
    }, 1200);
  }

  // ---------------------------------------------------------------- copy, link, print and the address

  function linkHash() {
    var p = ['mix', 'md=' + state.mode];
    var add = function (k, id) { var v = val(id); if (v !== '') p.push(k + '=' + encodeURIComponent(v)); };
    if (state.mode === 'rate') {
      p.push('k=' + kind());
      ({ ratio: [['n', 'nm-n']], onein: [['n', 'nm-onein']], per: [['a', 'nm-amt'], ['u', 'nm-unit'], ['pl', 'nm-perl']], percent: [['p', 'nm-pct']] })[kind()]
        .forEach(function (x) { add(x[0], x[1]); });
    } else if (state.mode === 'ppm') { add('ppm', 'nm-ppm'); add('cs', 'nm-cs'); }
    else { add('a', 'nm-ha-amt'); add('u', 'nm-ha-unit'); add('o', 'nm-out'); add('ar', 'nm-area'); if (val('nm-area-unit') === 'ha') p.push('au=ha'); }
    if (state.mode !== 'ppm') add('s', 'nm-strength');
    add('v', 'nm-litres');
    if (state.chem && !state.chemAuto) p.push('c=' + encodeURIComponent(state.chem.id));
    return '#' + p.join('&');
  }
  // The mix goes into the address as it changes, so a reload, Back or a phone that reloads the tab brings it back (ALL-02).
  function saveLink() {
    if (!state.edited) return;
    clearTimeout(state.linkTimer);
    state.linkTimer = setTimeout(function () {
      try { history.replaceState(history.state, '', location.pathname + location.search + linkHash()); } catch (e) { /* ignore */ }
    }, 400);
  }

  // A value from a link is used only if it is a plain number as a person would type it (with the box's unit allowed),
  // short enough to be real. Anything else leaves the box empty (DIL-13).
  function clean(v, unit) {
    var s = String(v).trim();
    if (s.length > 12 || !/^[0-9.,:\sa-z%²/-]*$/i.test(s)) return '';
    var n = unit === 'n' ? M.ratioN(s) : unit === '1in' ? M.oneInN(s) : M.fieldNum(s, unit);
    return isFinite(n) ? s : '';
  }
  function restore() {
    var h = location.hash;
    if (h.indexOf('#mix') !== 0) return false;
    var p = {};
    h.slice(1).split('&').forEach(function (kv) {
      var b = kv.split('=');
      if (b[1] === undefined) return;
      try { p[b[0]] = decodeURIComponent(b[1]); } catch (e) { p[b[0]] = ''; }
    });
    if (p.md === 'ppm' || p.md === 'hectare' || p.md === 'rate') setMode(p.md, true);
    if (state.mode === 'rate' && p.k) {
      var radio = /^(ratio|onein|per|percent)$/.test(p.k) ? $('input[name="nm-kind"][value="' + p.k + '"]') : null;
      if (radio) { radio.checked = true; showKind(); }
      var k = kind();
      if (k === 'ratio' && p.n) setVal('nm-n', clean(p.n, 'n'));
      if (k === 'onein' && p.n) setVal('nm-onein', clean(p.n, '1in'));
      if (k === 'per') { if (p.u === 'l' || p.u === 'ml') setVal('nm-unit', p.u); if (p.a) setVal('nm-amt', clean(p.a, val('nm-unit'))); if (p.pl) setVal('nm-perl', clean(p.pl, 'l')); }
      if (k === 'percent' && p.p) setVal('nm-pct', clean(p.p, 'pct'));
    }
    if (state.mode === 'ppm') { if (p.ppm) setVal('nm-ppm', clean(p.ppm, 'ppm')); if (p.cs) setVal('nm-cs', clean(p.cs, 'pct')); }
    if (state.mode === 'hectare') {
      if (p.u === 'l' || p.u === 'ml') setVal('nm-ha-unit', p.u);
      if (p.a) setVal('nm-ha-amt', clean(p.a, val('nm-ha-unit')));
      if (p.o) setVal('nm-out', clean(p.o, 'out'));
      if (p.au === 'ha') setVal('nm-area-unit', 'ha');
      if (p.ar) setVal('nm-area', clean(p.ar, p.au === 'ha' ? 'ha' : 'm2'));
    }
    if (p.s && state.mode !== 'ppm') setVal('nm-strength', clean(p.s, 'pct'));
    if (p.v) setVal('nm-litres', clean(p.v, 'l'));
    if (p.c) {
      var id = String(p.c).toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 60);
      state.strengthFor = id;
      setChem(id, false, true);
    }
    return true;
  }

  function copyText(text, msg) {
    var flag = $('.nm-copied');
    var done = function () { if (flag) { flag.textContent = msg; setTimeout(function () { flag.textContent = ''; }, 2500); } };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(text).then(done, function () { fallback(text); done(); }); return; }
    } catch (e) { /* fall through */ }
    fallback(text);
    done();
  }
  function fallback(text) {
    var t = document.createElement('textarea');
    t.value = text; t.setAttribute('readonly', ''); t.style.position = 'absolute'; t.style.left = '-9999px';
    document.body.appendChild(t); t.select();
    try { document.execCommand('copy'); } catch (e) { /* ignore */ }
    document.body.removeChild(t);
  }
  function shareUrl() {
    var h = linkHash();
    try { history.replaceState(history.state, '', location.pathname + location.search + h); } catch (err) { /* ignore */ }
    return location.origin + location.pathname + location.search + h;
  }

  // The mix as plain text, with the area line on the per hectare tab (DIL-16).
  function resultText() {
    var L = state.last;
    if (!L) return '';
    var lines = [L.size + ' of mix, ' + M.describe(L.rate) + ':'];
    lines.push(L.res.neat ? 'use the concentrate neat' : L.conc + ' concentrate + ' + L.water + ' water');
    if (state.mode === 'hectare') {
      lines.push('One tank covers ' + M.fmtNum(L.res.coverHa, L.res.coverHa < 1 ? 3 : 2) + ' ha');
      if (state.jobText) lines.push(state.jobText);
    }
    if (state.chem) lines.push(chemLine(L.res));
    lines.push('NORA PRO dilution calculator: ' + location.origin + location.pathname + linkHash());
    return lines.join('\n');
  }

  // Print keeps only the answer card (html.nora-print-nm); a normal print leaves the tool out, as on the other calculators.
  function printCard() {
    var html = document.documentElement;
    html.classList.add('nora-print-nm');
    var done = function () { html.classList.remove('nora-print-nm'); window.removeEventListener('afterprint', done); };
    window.addEventListener('afterprint', done);
    try { window.print(); } catch (e) { /* ignore */ }
    setTimeout(function () { if (html.classList.contains('nora-print-nm')) done(); }, 1000);
  }

  // ---------------------------------------------------------------- mode, kind and size controls

  function setMode(mode, quiet) {
    var was = state.mode;
    state.mode = mode;
    $$('[data-nm-mode]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-nm-mode') === mode ? 'true' : 'false'); });
    $$('[data-nm-panel]').forEach(function (p) { p.hidden = p.getAttribute('data-nm-panel') !== mode; });
    $$('[data-nm-only]').forEach(function (p) { p.hidden = p.getAttribute('data-nm-only') !== (mode === 'hectare' ? 'hectare' : 'mix'); });
    root.setAttribute('data-mode', mode);
    $('#nm-strength-row').hidden = mode === 'ppm';
    $('#nm-litres-label').textContent = mode === 'hectare' ? 'Tank size' : 'How much mix are you making?';
    // The sanitiser tab checks bleach only. A chemical picked on another tab is put aside and comes back after (DIL-15).
    if (mode === 'ppm') {
      if (state.chem && !state.chemAuto && state.chem.group !== 'bleach') {
        state.userChem = { chem: state.chem, strength: val('nm-strength') };
        state.chem = null;
      }
      if (!state.chem) setChem('bleach', true);
    } else if (was === 'ppm' || !quiet) {
      if (state.chemAuto) { state.chem = null; state.chemAuto = false; setVal('nm-chem', ''); }
      if (state.userChem && !state.chem) {
        state.chem = state.userChem.chem; state.chemAuto = false; state.strengthFor = state.chem.id;
        setVal('nm-chem', state.chem.name); setVal('nm-strength', state.userChem.strength);
      }
      state.userChem = null;
    }
    if (!quiet) { firstUse(); render(); }
  }

  function showKind() {
    var k = kind();
    $$('[data-nm-kind]').forEach(function (g) { g.hidden = g.getAttribute('data-nm-kind').split(' ').indexOf(k) < 0; });
  }

  // ---------------------------------------------------------------- chemical search

  // Everyday names first when several match equally ("acid" lists hydrochloric before nitric) (DIL-14).
  var COMMON = ['bleach', 'hydrochloric-acid', 'phosphoric-acid', 'citric-acid', 'acetic-acid', 'sulphuric-acid', 'oxalic-acid',
    'sodium-hydroxide', 'isopropyl-alcohol', 'ethanol', 'plant-protection', 'acid-wash', 'water-based-cleaner', 'solvent-degreaser'];
  function rank(row) { var i = COMMON.indexOf(row.id); return i < 0 ? 99 : i; }
  function closeList() { var box = $('#nm-chem-sug'); if (box) box.innerHTML = ''; }

  function suggest() {
    var q = val('nm-chem');
    var box = $('#nm-chem-sug');
    if (state.chem && q === state.chem.name) { closeList(); return; }
    state.chem = null;
    state.chemAuto = false;
    // A cleared chemical box clears its strength too (DIL-01).
    if (q.trim() === '') { setVal('nm-strength', ''); state.strengthFor = null; }
    if (q.trim().length < 2) { closeList(); render(); return; }
    load().then(function (d) {
      if (val('nm-chem') !== q) return;
      if (!d) {
        box.innerHTML = '<p class="nm-small">We could not get the chemical list just now. Your mixing answer above is not affected. ' +
          '<button type="button" class="nm-act" data-nm-retry>Try again</button></p>';
        render();
        return;
      }
      var res = E.lookup(d, q, 40);
      var parsed = res.parsed.strength !== null && state.mode !== 'ppm';
      if (parsed) setVal('nm-strength', String(res.parsed.strength));
      var order = function (a, b) { return b.score - a.score || rank(a.row) - rank(b.row); };
      res.matches.sort(order);
      var list = res.matches.length ? res.matches : res.suggestions;
      if (res.matches.length && E.isClear(res) && res.matches[0].score >= 100) {
        adopt(res.matches[0].row, false, parsed);
        closeList();
        render();
        return;
      }
      if (!list.length) {
        box.innerHTML = '<p class="nm-small" role="status">No match in our guide. <a class="nm-link" href="' + esc(quoteBase + '?chemical=' + encodeURIComponent(q.trim())) + '">Ask us about it</a>.</p>';
        render();
        return;
      }
      var shown = list.slice(0, 6);
      box.innerHTML = '<p class="nm-small nm-sug__count" role="status">' + (res.matches.length ? (shown.length === 1 ? '1 match. Tap it to check it.' : shown.length + ' matches. Tap the one you mean.') : 'Did you mean:') + '</p>' + shown.map(function (x) {
        return '<button type="button" class="nm-sug" data-nm-chem="' + esc(x.row.id) + '">' + esc(x.row.name) + '</button>';
      }).join('');
      state.pendingStrength = parsed;
      render();
    });
  }

  // ---------------------------------------------------------------- events

  function goToFirstError() {
    var e = state.errs[0];
    var el = e ? $('#' + e.id) : null;
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  }

  root.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('button, a') : null;
    if (!t || !root.contains(t)) return;
    if (t.hasAttribute('data-nm-mode')) { setMode(t.getAttribute('data-nm-mode')); return; }
    if (t.hasAttribute('data-nm-size')) { setVal('nm-litres', t.getAttribute('data-nm-size')); firstUse(); render(); return; }
    if (t.hasAttribute('data-nm-chem')) { firstUse(); setChem(t.getAttribute('data-nm-chem'), false, state.pendingStrength); state.pendingStrength = false; return; }
    if (t.hasAttribute('data-nm-ppm')) { setVal('nm-ppm', t.getAttribute('data-nm-ppm')); firstUse(); render(); return; }
    if (t.hasAttribute('data-nm-cs')) { setVal('nm-cs', t.getAttribute('data-nm-cs')); if (!state.chem) setChem('bleach', true); firstUse(); render(); return; }
    if (t.hasAttribute('data-nm-cal')) { calibrate(); return; }
    if (t.id === 'nm-peek') {
      if (!state.last && state.errs.length) { goToFirstError(); return; }
      // The top of the card, clear of the site header that slides back in on the way up. On desktop the card is
      // sticky, so the place to go is the top of its column, where the card starts (as on the Delta T Calculator).
      var card = $('#nm-result .nm-card');
      if (card) {
        var smooth = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        var wide = window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
        var from = wide ? $('.nm-side') : card;
        var top = from.getBoundingClientRect().top + window.pageYOffset - (state.headerH || 0) - 16;
        try { window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' }); } catch (err) { window.scrollTo(0, Math.max(0, top)); }
        try { card.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
      }
      return;
    }
    if (t.hasAttribute('data-nm-clear')) { setChem(null); return; }
    if (t.hasAttribute('data-nm-retry')) { state.loading = null; suggest(); return; }
    if (t.hasAttribute('data-nm-copy')) { copyText(resultText(), 'Copied. Paste it into a message or your records.'); track('guide_share', { guide_kind: state.mode, guide_via: 'copy_result' }); return; }
    if (t.hasAttribute('data-nm-link')) { copyText(shareUrl(), 'Link copied.'); track('guide_share', { guide_kind: state.mode, guide_via: 'copy_link' }); return; }
    if (t.hasAttribute('data-nm-print')) { track('guide_share', { guide_kind: state.mode, guide_via: 'print' }); printCard(); return; }
    if (t.hasAttribute('data-nm-quote')) {
      track('guide_quote_click', { guide_kind: state.mode, guide_chemical: state.chem ? state.chem.id : undefined });
    }
  });

  // The quick sprayer test (DIL-07): works from its button or Enter in either box, and checks the answer makes sense.
  function calibrate() {
    var o = M.outputPerHa(val('nm-cal-l'), val('nm-cal-a'));
    var msg = $('#nm-cal-msg');
    if (o === null) { msg.textContent = 'Enter the litres you sprayed and the area in square metres.'; msg.className = 'nm-cal-msg nm-cal-msg--warn'; return; }
    setVal('nm-out', String(Math.round(o)));
    if (o > M.OUTPUT_HIGH) {
      msg.innerHTML = '<b>Check:</b> that is about ' + esc(M.fmtNum(o, 0)) + ' L per hectare, far more than a hand or knapsack sprayer puts out (about 100 to 500 L per hectare). Check the area is in square metres, not hectares. Added above.';
      msg.className = 'nm-cal-msg nm-cal-msg--warn';
    } else {
      msg.textContent = 'Your sprayer puts out about ' + M.fmtNum(o, 0) + ' L per hectare. Added above.';
      msg.className = 'nm-cal-msg';
    }
    firstUse();
    render();
  }

  root.addEventListener('focusout', function (e) {
    if (e.target.id && e.target.classList.contains('nm-in')) { state.touched[e.target.id] = true; markFields(M.mix(currentRate(), val('nm-litres'), { strength: state.mode === 'ppm' ? null : val('nm-strength') })); }
    setTimeout(peek, 0);
  });
  // Tapping or tabbing into a number box selects its value, so typing replaces it (10 then 20 gives 20, not 1020).
  // The click that focused the box would clear the selection on release, so that one release is cancelled.
  var selectOnUp = null;
  function selectAll(t) { try { t.setSelectionRange(0, t.value.length); } catch (err) { try { t.select(); } catch (e2) { /* ignore */ } } }
  root.addEventListener('focusin', function (e) {
    peek();
    var t = e.target;
    if (!(t.classList && t.classList.contains('nm-in') && t.getAttribute('inputmode') === 'decimal')) return;
    selectAll(t);
    selectOnUp = t;
    t.setAttribute('data-nm-focus-value', t.value);
    setTimeout(function () { if (selectOnUp === t) selectOnUp = null; }, 600);
  });
  ['mouseup', 'touchend'].forEach(function (type) {
    root.addEventListener(type, function (e) {
      if (selectOnUp && e.target === selectOnUp) {
        var t = selectOnUp;
        selectOnUp = null;
        if (type === 'mouseup') e.preventDefault();
        // Only if nothing has been typed yet, so a fast typist never loses a digit.
        setTimeout(function () { if (document.activeElement === t && t.value === t.getAttribute('data-nm-focus-value')) selectAll(t); }, 0);
      }
    });
  });
  root.addEventListener('keydown', function (e) {
    var t = e.target;
    // Escape closes the chemical list (DIL-14).
    if (e.key === 'Escape' && (t.id === 'nm-chem' || (t.classList && t.classList.contains('nm-sug')))) {
      if ($('#nm-chem-sug').innerHTML) { e.preventDefault(); closeList(); $('#nm-chem').focus(); }
      return;
    }
    if (e.key !== 'Enter' || !t.classList || !t.classList.contains('nm-in')) return;
    e.preventDefault();
    if (t.id === 'nm-cal-l' || t.id === 'nm-cal-a') {
      if (t.id === 'nm-cal-l' && val('nm-cal-a').trim() === '') { $('#nm-cal-a').focus(); return; }
      calibrate();
      return;
    }
    if (t.id === 'nm-chem') {
      // Enter picks a match only when there is one; with several, it moves to the list so the visitor picks.
      var sugs = $$('#nm-chem-sug .nm-sug');
      if (sugs.length === 1) { sugs[0].click(); t.blur(); }
      else if (sugs.length) sugs[0].focus();
      else t.blur();
      return;
    }
    t.blur();
  });

  root.addEventListener('input', function (e) {
    var id = e.target.id;
    if (!id) return;
    firstUse();
    if (id === 'nm-chem') { suggest(); return; }
    if (id === 'nm-strength') state.strengthFor = state.chem ? state.chem.id : null;
    if (id === 'nm-amt') delete state.cleared['nm-amt'];
    render();
  });
  root.addEventListener('change', function (e) {
    if (e.target.name === 'nm-kind') { showKind(); firstUse(); render(); return; }
    if (e.target.tagName === 'SELECT') {
      // A new unit makes the old amount wrong (100 mL is not 100 L), so the amount box is cleared and the card says why.
      if (e.target.id === 'nm-unit' && val('nm-amt').trim() !== '') { setVal('nm-amt', ''); state.cleared['nm-amt'] = true; state.touched['nm-amt'] = true; }
      firstUse();
      render();
    }
  });
  var form = $('#nm-form');
  if (form) form.addEventListener('submit', function (e) { e.preventDefault(); if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); render(); });

  // ---------------------------------------------------------------- start

  // One message line under each row of boxes, for the words that explain a red box (ALL-01).
  $$('.nm-row').forEach(function (row, i) {
    if (!row.querySelector('.nm-in')) return;
    var p = document.createElement('p');
    p.className = 'nm-err';
    p.id = 'nm-err-' + i;
    p.hidden = true;
    row.setAttribute('data-err', p.id);
    row.parentNode.insertBefore(p, row.nextSibling);
  });
  showKind();
  var restored = restore();
  // A share link opened in a tab already on this page changes only the part after #, so the browser does not load the
  // page again and the old figures stayed. Load it again, so the new link is read like any other (ALL-R3). The tool
  // writes its own link with replaceState, which does not fire hashchange.
  window.addEventListener('hashchange', function () { if (location.hash.indexOf('#mix&') === 0) location.reload(); });
  if (!restored) setMode('rate', true);
  render();
  stickyTop();
  window.addEventListener('resize', stickyTop);
  watchPeek();
  root.setAttribute('data-ready', '1');
  // A reload or Back also restores the mix from the address; only a link opened fresh counts as restored.
  var nav = '';
  try { nav = (performance.getEntriesByType('navigation')[0] || {}).type || ''; } catch (e) { /* ignore */ }
  if (restored) {
    state.used = true;
    state.edited = true;
    if (nav !== 'reload' && nav !== 'back_forward') track('guide_result_shown', { guide_kind: state.mode, guide_restored: 1 });
  }
})();
